'use client';

import { useState } from 'react';
import { FileText } from 'lucide-react';
import { useRoomContext } from '@livekit/components-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SummaryButtonProps {
  onSummaryClick: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Summary button component that sits next to the Hang Up button
 * Extracts conversation transcript and triggers clinical note generation
 */
export function SummaryButton({ onSummaryClick, disabled = false, className }: SummaryButtonProps) {
  const room = useRoomContext();
  const [isExtracting, setIsExtracting] = useState(false);

  const handleSummaryClick = async () => {
    setIsExtracting(true);

    try {
      // Parent component handles transcript generation from messages
      onSummaryClick();
    } catch (error) {
      console.error('Failed to trigger summary:', error);
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <Button
      variant="secondary"
      onClick={handleSummaryClick}
      disabled={disabled || isExtracting}
      className={cn(
        'flex items-center gap-2 font-mono',
        isExtracting && 'cursor-not-allowed opacity-50',
        className
      )}
      aria-label="生成临床记录总结"
    >
      <FileText className="h-4 w-4" />
      <span className="hidden md:inline">{isExtracting ? '提取中...' : '回顾心声'}</span>
      <span className="inline md:hidden">{isExtracting ? '...' : '回顾心声'}</span>
    </Button>
  );
}
